import { useState, useContext } from 'react';
import { useSelector } from 'react-redux';

import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';

import ResourceModal from 'app/components/ResourceModal';
import ResourceModalPanel from 'app/components/ResourceModalPanel';
import DefaultMomentsUploadModal from 'app/components/Uploads/DefaultMomentsUploadModal';
import CustomersUploadModal from 'app/components/Uploads/CustomersUploadModal';
import CustomerUploadModal from 'app/components/Uploads/CustomerUploadModal';
import useDefaultMomentsUploadForm from 'app/hooks/useDefaultMomentsUploadForm';
import useCustomersUploadForm from 'app/hooks/useCustomersUploadForm';
import useDeeplink from 'app/hooks/useDeeplink';
import JobForm from './JobForm';
import DefaultMomentsUploadProgressBar from './DefaultMomentsUploadProgressBar';
import CustomersUploadProgressBar from './CustomersUploadProgressBar';
import UploadModal from 'app/components/Uploads/UploadModal';
import useQrCodeTemplateUploadForm from 'app/hooks/useQrCodeTemplateUploadForm';
import useCustomers from 'app/hooks/useCustomers';
import CustomerModal from 'app/components/Customers/CustomerModal';
import UploadPreview from 'app/components/PhotoSwipe/UploadPreview';
import QrCodeTemplateUploadProgressBar from './QrCodeTemplateUploadProgressBar';
import UploadWorker from 'app/workers/UploadWorker';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  //notifyCustomers,
  //destroyJob,
  deleteDefaultMoment,
  getJob
} from 'app/services/jobs';

import {
  getMoments
} from 'app/services/moments';

import {
  customers,
  moments
} from 'app/resources';

import {
  JOB_QRCODE_URL,
  JOB_QRCODE_TEMPLATE_URL,
  JOB_QRCODE_TEMPLATE_UPLOAD_URL
} from 'app/utils/Constants';

import {
  formatPercent,
  cacheBustUrl
} from 'shared/utils/Utils';

const JobPanel = props => {
  const {
    job,
    action,
    onDelete,
    onUploadStart,
    onUploadComplete,
    account,
    updateRow
  } = props;

  const uploadWorker = useContext(UploadWorker);

  const showAddCustomer = !account.feature_flags.disable_add_customer;
  const showDefaultMoments = account.feature_flags.default_moments;
  const showMomentUpload = !account.feature_flags.disable_upload;

  const onCustomerCreate = async () => {
    // refresh job in modal & grid to update customer count
    let updatedJob = await getJob(job.id);
    updateRow(updatedJob);
  }

  const {
    formIsOpen: customerFormIsOpen,
    onFormClose: onCustomerFormClose,
    onAdd: addCustomer,
    onFormSubmit: onCustomerFormSubmit
  } = useCustomers({
    onCreate: onCustomerCreate
  });

  const {
    formIsOpen: defaultMomentsFormIsOpen,
    onAdd: uploadDefaultMoments,
    onFormClose: onDefaultMomentsFormClose,
    onFormSubmit: onDefaultMomentsFormSubmit
  } = useDefaultMomentsUploadForm({
    onUploadStart,
    onUploadComplete,
    job
  });

  const {
    formIsOpen: customerMomentsFormIsOpen,
    onAdd: uploadCustomerMoments,
    onFormClose: onCustomerMomentsFormClose,
    onFormSubmit: onCustomerMomentsFormSubmit
  } = useCustomersUploadForm({
    onUploadStart,
    onUploadComplete,
    job
  });

  // setup QR code template Photoswipe item.
  // during upload it will be set to an UploadPreview component,
  // otherwise the src will be set
  const qrCodeTemplateUrl = JOB_QRCODE_TEMPLATE_URL(job.id);
  const [qrCodeTemplateImage, setQrCodeTemplateImage] = useState({
    src: cacheBustUrl(qrCodeTemplateUrl, Date.now())
  })

  const onQrCodeTemplateUploadStart = file => {
    uploadWorker.createObjectURL(file, url => {
      setQrCodeTemplateImage({
        jsx: <UploadPreview src={url} width={720} height={720} progressBar={<QrCodeTemplateUploadProgressBar job={job} />} />
      });
    });
  }

  const onQrCodeTemplateUploadComplete = async () => {
    let updatedJob = await getJob(job.id);
    updateRow(updatedJob);

    setQrCodeTemplateImage({
      src: cacheBustUrl(qrCodeTemplateUrl, Date.now())
    });
  }

  const {
    formIsOpen: qrCodeTemplateFormIsOpen,
    onAdd: uploadQrCodeTemplate,
    onFormClose: onQrCodeTemplateFormClose,
    onFormSubmit: onQrCodeTemplateFormSubmit,
  } = useQrCodeTemplateUploadForm({
    onUploadStart: onQrCodeTemplateUploadStart,
    onUploadComplete: onQrCodeTemplateUploadComplete,
    job
  });

  const deeplink = useDeeplink();

  // if we're creating a job then no actions panel will be shown
  if (action === 'create') {
    return null;
  }

  // get images for Default Moments card
  const getDefaultImages = async (page = 1, perPage = 100) => {
    let filterModel = {
      story_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: job.template_story_id.toString()
      }],
    }

    let params = {
      page,
      per_page: perPage,
      filter_model: filterModel,
      sort_model: []
    }

    let resp = await getMoments(params);

    let images = resp.records.map(moment => {
      return {
        id: moment.id,
        src: moment.preview_url,
        w: moment.width,
        h: moment.height,
        filename: moment.filename,
        original: moment.print_url,
        is_video: moment.type && moment.type.includes("video")
      }
    });

    return images;
  }

  // get images for Customer Moments card
  const getCustomerImages = async (page = 1, perPage = 10) => {
    let filterModel = {
      job_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: job.id.toString()
      }],
      is_default: [{
        filterClass: 'text',
        filterType: 'equals',
        value: '0'
      }]
    }

    let params = {
      page,
      per_page: perPage,
      filter_model: filterModel,
      sort_model: []
    }

    let resp = await getMoments(params);

    let images = resp.records.map(moment => {
      return {
        id: moment.id,
        src: moment.preview_url,
        w: moment.width,
        h: moment.height,
        filename: moment.filename,
        original: moment.print_url,
        is_video: moment.type && moment.type.includes("video")
      }
    });

    return images;
  }

  const deleteMoment = async (moment) => {
    return await deleteDefaultMoment(job.id, moment.id);
  }

  const viewMoment = (moment, newTab = false) => {
    let filterModel = {
      id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: moment.id.toString(),
        operator: AND_OPERATOR
      }]
    }

    if (newTab) {
      // return link to set as anchor href with target="_blank"
      return deeplink.getLink(moments, filterModel);
    } else {
      deeplink.load(moments, filterModel);
    }
  }

  const getDefaultMomentsLink = () => {
    let filterModel = {
      job_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: job.id.toString(),
        operator: AND_OPERATOR
      }],
      is_default: [{
        filterClass: 'text',
        filterType: 'equals',
        value: '1',
        operator: AND_OPERATOR
      }]
    }

    // return link to set as anchor href with target="_blank"
    return deeplink.getLink(moments, filterModel);
  }

  const getCustomerMomentsLink = () => {
    let filterModel = {
      job_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: job.id.toString(),
        operator: AND_OPERATOR
      }],
      is_default: [{
        filterClass: 'text',
        filterType: 'equals',
        value: '0',
        operator: AND_OPERATOR
      }]
    }

    // return link to set as anchor href with target="_blank"
    return deeplink.getLink(moments, filterModel);
  }

  const getCustomersLink = () => {
    let filterModel = {
      job_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: job.id.toString(),
        operator: AND_OPERATOR
      }]
    }

    // return link to set as anchor href with target="_blank"
    return deeplink.getLink(customers, filterModel);
  }

  const downloadQrCode = () => {
    const link = document.createElement('a');
    link.href = cacheBustUrl(JOB_QRCODE_URL(job.id));
    link.download = 'qrcode.png';
    link.target = '_blank';
    link.click();
  };

  const downloadQrCodeTemplate = () => {
    const link = document.createElement('a');
    link.href = cacheBustUrl(JOB_QRCODE_TEMPLATE_URL(job.id));
    link.download = 'qrcode-template.png';
    link.target = '_blank';
    link.click();
  };

  let customerActions = [];
  if (showAddCustomer) {
    customerActions.push(
      { label: 'Add Customer', callback: addCustomer }
    );
  }
  customerActions.push(
    { label: 'View Customers', newTabLink: getCustomersLink() }
  );

  let defaultMomentActions = [];
  let customerMomentActions = [];
  if (showMomentUpload) {
    defaultMomentActions.push(
      { label: 'Upload Moments', callback: uploadDefaultMoments }
    );
    customerMomentActions.push(
      { label: 'Upload Moments', callback: uploadCustomerMoments }
    );
  }
  defaultMomentActions.push(
    { label: 'View Moments', newTabLink: getDefaultMomentsLink() }
  );
  customerMomentActions.push(
    { label: 'View Moments', newTabLink: getCustomerMomentsLink() }
  );

  let cards = [
    {
      label: 'Customers',
      details: [
        { label: 'Customers', value: job.customer_count },
        { label: '% Viewed', value: formatPercent(job.percent_viewed) },
        { label: '% Purchased', value: formatPercent(job.percent_purchased) }
      ],
      actions: customerActions
    }
  ]

  if (showDefaultMoments) {
    cards.push({
      label: 'Default Moments',
      visible: showDefaultMoments,
      getImages: getDefaultImages,
      imageCount: job.expected_photo_count,
      imagesPerPage: 100,
      progressBar: <DefaultMomentsUploadProgressBar job={job} inModal={true} />,
      details: [
        { label: 'Default Moments', value: job.actual_photo_count }
      ],
      footnote: 'Default moments will appear for every customer',
      actions: defaultMomentActions,
      psActions: [
        { key: "delete", icon: DeleteIcon, onClick: deleteMoment },
        { key: "link", icon: LinkIcon, onClick: viewMoment },
      ]
    });
  }

  cards = cards.concat([
    {
      label: 'Customer Moments',
      getImages: getCustomerImages,
      imageCount: job.expected_customer_photo_count,
      imagesPerPage: 100,
      progressBar: <CustomersUploadProgressBar job={job} inModal={true} />,
      details: [
        { label: 'Customer Moments', value: job.customer_photo_count }
      ],
      footnote: 'These moments have been assigned to individual customers',
      actions: customerMomentActions,
      psActions: [
        { key: "link", icon: LinkIcon, onClick: viewMoment },
      ]
    },
    {
      label: 'QR Code',
      visible: account.feature_flags.qr_code_workflow,
      message: 'The customer can use this QR code to register themselves on the job',
      image: {
        id: 'qr-code',
        src: JOB_QRCODE_URL(job.id),
        w: 445,
        h: 445
      },
      actions: [
        { label: 'Download QR Code', callback: downloadQrCode }
      ]
    },
    {
      label: 'QR Code Template',
      visible: account.feature_flags.qr_code_workflow,
      message: 'The customer\'s QR code will appear on this template',
      image: job.has_qr_template && {
        id: 'qr-code-template',
        w: 720,
        h: 720,
        ...qrCodeTemplateImage
      },
      actions: [
        { label: 'Upload Template', callback: uploadQrCodeTemplate },
        { label: 'Download Template', callback: downloadQrCodeTemplate }
      ]
    },
    {
      label: 'Delete Job',
      message: 'Deleting a job will permanently remove its customers and images.',
      actions: [
        { label: 'Delete Job', callback: onDelete }
      ]
    }
  ]);

  return (
    <div>
      <ResourceModalPanel cards={cards} />
      <DefaultMomentsUploadModal
        isOpen={defaultMomentsFormIsOpen}
        onClose={onDefaultMomentsFormClose}
        onFormSubmit={onDefaultMomentsFormSubmit}
        job={job}
        center={true}
      />
      <CustomersUploadModal
        isOpen={customerMomentsFormIsOpen}
        onClose={onCustomerMomentsFormClose}
        onFormSubmit={onCustomerMomentsFormSubmit}
        job={job}
        center={true}
      />
      <UploadModal
        title="Upload QR Code Template"
        message="This template will surround the customer's QR code"
        isOpen={qrCodeTemplateFormIsOpen}
        onClose={onQrCodeTemplateFormClose}
        onFormSubmit={onQrCodeTemplateFormSubmit}
        jobId={job.id}
        center={true}
      />
      <CustomerModal
        isOpen={customerFormIsOpen}
        onClose={onCustomerFormClose}
        onFormSubmit={onCustomerFormSubmit}
        job={job}
        center={true}
      />
    </div>
  );
}

const JobModal = props => {
  const {
    isOpen,
    onClose,
    onFormSubmit,
    onDestroy,
    job,
    jobConfigs,
    kiosks,
    onUploadStart,
    onUploadComplete,
    updateRow
  } = props;

  const account = useSelector(state => state.portalReducer.account);

  const action = job ? 'update' : 'create';

  let title, subTitle = null;
  if (action === 'update') {
    title = job.name;
    subTitle = job.event_id;
  } else {
    title = 'New Job';
  }

  const onSubmit = params => onFormSubmit(action, params)
  const onDelete = () => onDestroy(job.id);

  let panel = null;
  if (job) {
    panel = <JobPanel {...{ job, action, onClose, onDelete, onUploadStart, onUploadComplete, account, updateRow }} />;
  }

  return (
    <ResourceModal {...{ isOpen, onClose, title, subTitle }}>
      {panel}
      <JobForm {...{ job, action, onClose, onSubmit, jobConfigs, kiosks, account }} />
    </ResourceModal>
  );
}

export default JobModal;
